import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Cinematic studio reel — autoplay/loop/muted MP4 with ghost-gradient
 * radial fade so the edges dissolve into the black page background.
 * "ON AIR" HUD chip pulses over the video.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const VIDEO_SRC = `${BACKEND}/api/static/videos/studio_reel.mp4`;

const StudioReelVideo = () => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      className="col-span-2 relative h-56 md:h-72 overflow-hidden"
      data-testid="studio-reel-video"
      aria-hidden="true"
    >
      {/* Ghost-gradient mask container — no hard border, dissolves to black */}
      <div
        className="absolute inset-0"
        style={{
          maskImage:
            "radial-gradient(ellipse 95% 92% at 50% 50%, rgba(0,0,0,1) 35%, rgba(0,0,0,0.65) 70%, rgba(0,0,0,0) 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 95% 92% at 50% 50%, rgba(0,0,0,1) 35%, rgba(0,0,0,0.65) 70%, rgba(0,0,0,0) 100%)",
        }}
      >
        <video
          src={VIDEO_SRC}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          onLoadedData={() => setLoaded(true)}
          data-testid="studio-reel-video-el"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Subtle warm/cool overlay to match site palette */}
        <div
          className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-40"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,0,85,0.18) 0%, transparent 45%, rgba(0,240,255,0.18) 100%)",
          }}
        />
        {/* Soft vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.55) 100%)",
          }}
        />
      </div>

      {/* Loading shimmer until video is ready */}
      <AnimatePresence>
        {!loaded && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <motion.div
              className="w-12 h-12 rounded-full border-2 border-[#00f0ff]/30 border-t-[#00f0ff]"
              animate={{ rotate: 360 }}
              transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ON AIR HUD chip */}
      <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur-sm border border-[#ff0055]/40">
        <motion.span
          className="w-1.5 h-1.5 rounded-full bg-[#ff0055]"
          animate={{ opacity: [1, 0.25, 1], scale: [1, 1.4, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        />
        <span className="text-[9px] uppercase tracking-[0.32em] font-bold text-[#ff0055]">
          On Air
        </span>
      </div>

      {/* Studio label HUD bottom-left */}
      <div className="absolute bottom-3 left-3 z-10 text-[9px] uppercase tracking-[0.32em] font-semibold text-[#00f0ff]/80">
        [ SB Media · Live Studio ]
      </div>
    </div>
  );
};

export default StudioReelVideo;
